/**
 * Property Valuation Widget
 *
 * Main entry point for the property valuation widget.
 * Extends the base Widget class with property valuation specific functionality.
 */

import Widget from "@core/Widget";
import "./styles/valuation.css";
import "mapbox-gl/dist/mapbox-gl.css";

// Import widget-specific components
import { initializeAddressSearch } from "./components/AddressSearch";
import { initializePropertyForm } from "./components/PropertyForm";
import { initializeValuationResult } from "./components/ValuationResult";

// Import services
import { submitValuation, sendSmsCode } from "./services/valuationApi.js";
import {
  getMapboxToken,
  fetchBuildingDetails,
} from "./services/geocodingService.js";

class PropertyValuationWidget extends Widget {
  constructor(options = {}) {
    // Widget-specific state
    const state = {
      selectedProperty: null,
      buildingDetails: null,
      propertyType: null,
      isApartmentBuilding: false,
      // Store form data across steps
      formData: {
        property: {},
        contact: {},
      },
    };

    super({
      name: "property-valuation-form",
      state,
    });

    // console.log("Property Valuation Widget initialized:", this.config);

    // Bind methods to ensure correct 'this' context
    this.showStep = this.showStep.bind(this);
    this.updateFormData = this.updateFormData.bind(this);
    this.updateContactData = this.updateContactData.bind(this);
    this.updateGoalData = this.updateGoalData.bind(this);
    this.selectOption = this.selectOption.bind(this);
    this.handleAddressSelection = this.handleAddressSelection.bind(this);
    this.checkSmsCode = this.checkSmsCode.bind(this);
  }

  /**
   * Initialize the widget
   */
  init() {
    super.init();

    // Initialize components
    initializeAddressSearch(this);
    initializePropertyForm(this);
    initializeValuationResult(this);

    // Add step change event listener
    document.addEventListener("click", (event) => {
      const stepButton = event.target.closest('[data-action="show-step"]');
      if (stepButton) {
        const step = stepButton.getAttribute("data-step");
        if (step) {
          event.preventDefault();
          this.showStep(step);
        }
      }
    });

    // Disable buttons initially
    this.disableButtons();

    // Initialize form data
    this.initializeFormData();

    // Set up property type select dropdown listener
    const propertyTypeSelect = document.getElementById('propertyTypeSelect');
    if (propertyTypeSelect) {
      propertyTypeSelect.addEventListener('change', (event) => {
        const propertyType = event.target.value;
        const landAreaSection = document.getElementById('land-area-section');
        
        // Show land area section only for houses
        if (landAreaSection) {
          landAreaSection.style.display = propertyType === 'house' ? 'block' : 'none';
        }
        
        // Clear land area value if switching to apartment
        if (propertyType === 'apartment') {
          const landAreaInput = document.getElementById('landAreaInput');
          if (landAreaInput) {
            landAreaInput.value = '';
            this.state.formData.property.land_area = null;
          }
        }
        
        // Update form data
        this.updateFormData('property_type', propertyType);
        this.updateFormData('property_category', propertyType);
      });
    }

    // Set up phone number input with default country code
    const phoneInput = document.getElementById("phoneInput");
    if (phoneInput) {
      phoneInput.value = "+41 ";
      phoneInput.addEventListener("focus", function () {
        if (!this.value) this.value = "+41 ";
      });

      // Ensure country code is not deleted
      phoneInput.addEventListener("input", () => {
        if (!phoneInput.value.startsWith("+41")) {
          phoneInput.value = `+41 ${phoneInput.value.replace(/\+41\s?/g, "")}`;
        }
      });
    }

    // Set up price input with auto-formatting
    const priceInput = document.querySelector("#pricing-field input");
    if (priceInput) {
      priceInput.placeholder = "CHF";

      priceInput.addEventListener("focus", function () {
        if (this.value === "") this.value = "CHF ";
      });

      priceInput.addEventListener("blur", function () {
        if (this.value === "CHF ") this.value = "";
      });
    }

    // Initiliaze link to privacy policy
    const privacyPolicyLink = document.getElementById("privacyPolicy");
    if (privacyPolicyLink) {
      privacyPolicyLink.href = this.config.privacyPolicyUrl;
    }
  }

  /**
   * Disable buttons initially
   */
  disableButtons() {
    const buttons = {
      verifyBtn: true,
      step1Next: true,
      step4Next: true,
      step5Next: true,
      step7Next: true,
    };

    Object.keys(buttons).forEach((id) => {
      const button = document.getElementById(id);
      if (button) button.disabled = true;
    });
  }

  /**
   * Initialize form data with default values from inputs
   */
  initializeFormData() {
    // Initialize property type from select dropdown
    const propertyTypeSelect = document.getElementById('propertyTypeSelect');
    if (propertyTypeSelect && propertyTypeSelect.value) {
      this.state.formData.property.property_type = propertyTypeSelect.value;
      this.state.formData.property.property_category = propertyTypeSelect.value;
    }

    const elements = {
      constructionYearInput: ["property", "construction_year", parseInt],
      livingAreaInput: ["property", "living_area", parseInt],
      landAreaInput: ["property", "land_area", parseInt],
      bedroomsInput: ["property", "rooms_nb", parseFloat],
      bathroomsInput: ["property", "bathrooms_nb", parseFloat],
      conditionSelect: ["property", "condition", (val) => val],
    };

    Object.keys(elements).forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        const [section, field, transform] = elements[id];
        this.state.formData[section][field] = transform(element.value);
      }
    });

  }

  /**
   * Show a specific step and update progress indicators
   * @param {number|string} stepNumber The step number to show
   */
  showStep(stepNumber) {
    // Special handling for step 2 to next step transition
    if (stepNumber === 3 && this.state.currentStep === 2) {
      this.handleStep2To3Transition();
      return;
    }

    // Normal flow for other steps
    this.updateStepVisibility(stepNumber);
  }

  /**
   * Handle transition from step 2 to step 3
   */
  handleStep2To3Transition() {
    // Create a loading overlay
    const step2 = document.getElementById("step2");
    if (step2) {
      const loadingOverlay = document.createElement("div");
      loadingOverlay.className = "loading-overlay";
      loadingOverlay.innerHTML = `
        <div class="loading-spinner"></div>
        <div class="loading-text">${this.getText("pleaseWait")}</div>
      `;
      step2.appendChild(loadingOverlay);
    }

    // Fetch building details
    fetchBuildingDetails(this.state.selectedProperty).then((details) => {
      // Remove loading overlay
      const overlay = document.querySelector(".loading-overlay");
      if (overlay) overlay.remove();

      // Store building details in state
      this.setState({
        buildingDetails: details,
        isApartmentBuilding: details && details.gklas === 1122,
        propertyType: details
          ? details.gklas === 1122
            ? "apartment"
            : "house"
          : null,
      });

      // Update form data with property type
      if (this.state.isApartmentBuilding) {
        this.state.formData.property.property_category = "apartment";
        this.state.formData.property.property_type = "apartment";
        
        // Update select dropdown selection
        const propertyTypeSelect = document.getElementById("propertyTypeSelect");
        if (propertyTypeSelect) propertyTypeSelect.value = "apartment";
        
        // Hide land area section for apartments
        const landAreaSection = document.getElementById("land-area-section");
        if (landAreaSection) landAreaSection.style.display = "none";
      } else if (
        details &&
        (details.gklas === 1110 || details.gklas === 1121)
      ) {
        this.state.formData.property.property_category = "house";
        this.state.formData.property.property_type = "house";

        // Update select dropdown selection
        const propertyTypeSelect = document.getElementById("propertyTypeSelect");
        if (propertyTypeSelect) propertyTypeSelect.value = "house";

        // Show land area section for houses
        const landAreaSection = document.getElementById("land-area-section");
        if (landAreaSection) landAreaSection.style.display = "block";
      }

      // update form data with building details
      if (details) {
        this.state.formData.property.egid = details.egid;
      }

      // Determine next step based on property type
      if (details && this.state.isApartmentBuilding) {
        // For apartments, check if we need to show apartment selection
        this.prepareApartmentUnits();
        this.updateStepVisibility("3b");
      } else {
        // For houses or if no apartment units, prefill and go directly to step 4
        this.prepareHouseUnit();
        this.updateStepVisibility(4);
      }
    });
  }

  /**
   * Update step visibility
   * @param {number|string} stepNumber The step number to show
   */
  updateStepVisibility(stepNumber) {
    // Hide all steps
    document.querySelectorAll(".step-content").forEach((step) => {
      step.classList.add("hidden-step");
    });

    // Show requested step
    if (stepNumber === "3b") {
      document.getElementById("step3b").classList.remove("hidden-step");
    } else {
      document
        .getElementById(`step${stepNumber}`)
        .classList.remove("hidden-step");
    }

    // Adjust progress indicator number to account for removed step 3
    const progressNumber =
      stepNumber === "3b"
        ? 3
        : parseInt(stepNumber) > 3
        ? parseInt(stepNumber) - 1
        : parseInt(stepNumber);

    // Update progress indicators
    document.querySelectorAll(".step").forEach((indicator, index) => {
      indicator.classList.toggle("active", index < progressNumber);
    });

    // Special handling for certain steps
    if (stepNumber === 2 && !this.map) {
      this.initializeMap();
    } else if (stepNumber === 6) {
      this.simulateLoading();
    } else if (stepNumber === 8) {
      this.handleSmsVerification();
    }

    // Track the current step for back functionality
    const currentStep = stepNumber === "3b" ? 3.5 : parseInt(stepNumber);
    this.setState({ currentStep });

    // Send height message to parent
    this.sendHeightToParent();
  }

  /**
   * Handle selection of an address from search results
   * @param {Object} result The selected address result
   */
  handleAddressSelection(result) {
    // Parse address components
    const addressParts = this.parseAddressComponents(result.attrs.label);

    // Store the selected property with featureId
    const selectedProperty = {
      address: result.attrs.label,
      street: addressParts.street,
      city: addressParts.city,
      zip: addressParts.zip || "1205", // Default
      country: "Switzerland", // Default
      lat: result.attrs.lat,
      lon: result.attrs.lon,
      id: result.id,
      featureId: result.attrs.featureId, // Store the featureId for the second API call
    };

    // Update state
    this.setState({ selectedProperty });

    // Update input fields with selected address
    document.getElementById("addressInput").value = result.attrs.label;
    document.getElementById("confirmedAddress").value = result.attrs.label;

    // Store in form data
    this.state.formData.property.coordinates = {
      latitude: result.attrs.lat,
      longitude: result.attrs.lon,
    };

    this.state.formData.property.address = {
      street: selectedProperty.street,
      city: selectedProperty.city,
      zip: selectedProperty.zip,
      country: selectedProperty.country,
    };

    // Hide results
    document.getElementById("addressResults").style.display = "none";

    // Move to step2
    this.showStep(2);
  }

  /**
   * Parse address into components
   * @param {string} addressString The address string to parse
   * @returns {Object} Address components
   */
  parseAddressComponents(addressString) {
    // Simplistic parser for address components
    const parts = addressString.split(" ");
    let city = parts[parts.length - 1]; // Last part is city

    // Check if the city contains a zip code
    let zip = "";
    if (/\d{4,5}/.test(city)) {
      zip = city.match(/\d{4,5}/)[0];
      city = city.replace(zip, "").trim();
    }

    // Rest is street
    const street = parts.slice(0, parts.length - 1).join(" ");

    return { street, city, zip };
  }

  /**
   * Update property form data
   * @param {string} field The field to update
   * @param {*} value The new value
   */
  updateFormData(field, value) {
    const transforms = {
      rooms_nb: parseFloat,
      bathrooms_nb: parseFloat,
      living_area: parseInt,
      land_area: parseInt,
      construction_year: parseInt,
    };

    const transform = transforms[field] || ((val) => val);
    this.state.formData.property[field] = transform(value);

    // Check if we can enable the continue button for step 4
    this.validateStep4Button();
  }

  /**
   * Update contact form data
   * @param {string} field The field to update
   * @param {*} value The new value
   */
  updateContactData(field, value) {
    // Special handling for specific fields
    if (field === "phone") {
      value = this.formatPhoneNumber(value);
    } else if (field === "email") {
      this.validateEmail(value);
    }

    this.state.formData.contact[field] = value;

    // Check if we can enable the continue button for step 7
    this.validateStep7Button();
  }

  /**
   * Format phone number with country code
   * @param {string} value The phone number value
   * @returns {string} Formatted phone number
   */
  formatPhoneNumber(value) {
    // Remove all non-digit characters
    let cleaned = value.replace(/\D/g, "");

    // If it doesn't start with country code, add it
    if (!cleaned.startsWith("41")) {
      cleaned = `41${cleaned}`;
    }

    // Format for display with country code
    const phoneInput = document.getElementById("phoneInput");
    if (phoneInput) {
      let formatted = `+${cleaned.substring(0, 2)}`;
      if (cleaned.length > 2) {
        formatted += ` ${cleaned.substring(2, 4)}`;
      }
      if (cleaned.length > 4) {
        formatted += ` ${cleaned.substring(4, 7)}`;
      }
      if (cleaned.length > 7) {
        formatted += ` ${cleaned.substring(7, 9)}`;
      }
      if (cleaned.length > 9) {
        formatted += ` ${cleaned.substring(9)}`;
      }

      phoneInput.value = formatted;
    }

    return cleaned;
  }

  /**
   * Validate email format
   * @param {string} email The email to validate
   */
  validateEmail(email) {
    const emailInput = document.getElementById("emailInput");
    if (!emailInput) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);

    emailInput.classList.toggle("error", !isValid);

    // Find or create error message element
    let errorMsg = document.getElementById("email-error");
    if (!errorMsg && !isValid) {
      errorMsg = document.createElement("div");
      errorMsg.id = "email-error";
      errorMsg.className = "error-message";
      errorMsg.textContent = "Please enter a valid email address";
      emailInput.parentNode.insertBefore(errorMsg, emailInput.nextSibling);
    } else if (errorMsg) {
      errorMsg.style.display = isValid ? "none" : "block";
    }
  }

  /**
   * Update goal data for valuation purpose
   * @param {string} field The field to update
   * @param {*} value The new value
   */
  updateGoalData(field, value) {
    // Special handling for expected price
    if (field === "expected_price") {
      value = this.formatPriceInput(value);
    }

    this.state.formData.contact[field] = value;

    // Check if we can enable the continue button for step 5
    this.validateStep5Button();
  }

  /**
   * Format price input in Swiss format (CHF 1'000'000)
   * @param {string} value The price value
   * @returns {number} The numeric price value
   */
  formatPriceInput(value) {
    // Store raw numeric value in form data
    const numericValue = parseInt(value.toString().replace(/\D/g, ""), 10);
    this.state.formData.contact.expected_price = numericValue;

    // Format for display
    const priceInput = document.querySelector("#pricing-field input");
    if (priceInput && !isNaN(numericValue)) {
      // Format with apostrophes for thousands
      const formatted = `CHF ${numericValue
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, "'")}`;
      priceInput.value = formatted;
    }

    return numericValue;
  }

  /**
   * Validation functions for buttons
   */
  validateStep4Button() {
    console.log("Validating Step 4 button...");
    const step4Button = document.getElementById("step4Next");
    if (!step4Button) return;

    const requiredFields = [
      "property_type",
      "rooms_nb",
      "bathrooms_nb",
      "living_area",
      "condition",
      "construction_year"
    ];
    const hasAllRequired = requiredFields.every(
      (field) => this.state.formData.property[field]
    );
    console.log(hasAllRequired);
    console.log(JSON.stringify(this.state));

    // If it's a house, land area is required
    const isHouse = this.state.formData.property.property_type === 'house';
    const isValid =
      hasAllRequired &&
      (!isHouse || this.state.formData.property.land_area);
    step4Button.disabled = !isValid;
  }

  validateStep5Button() {
    const step5Button = document.getElementById("step5Next");
    if (!step5Button) return;

    step5Button.disabled = !(
      this.state.formData.contact.reason &&
      this.state.formData.contact.expected_price
    );
  }

  validateStep7Button() {
    const step7Button = document.getElementById("step7Next");
    if (!step7Button) return;

    const requiredFields = ["first_name", "last_name", "phone", "consent"];
    const hasRequiredFields = requiredFields.every(
      (field) => this.state.formData.contact[field]
    );

    // For email, we also need to validate format
    const emailInput = document.getElementById("emailInput");
    const emailValid =
      emailInput &&
      this.state.formData.contact.email &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.state.formData.contact.email);

    step7Button.disabled = !(hasRequiredFields && emailValid);
  }

  /**
   * Select an option (for step 5 purpose options)
   * @param {HTMLElement} element The selected element
   * @param {string} groupSelector Selector for the option group
   */
  selectOption(element, groupSelector) {
    // Find all siblings with the same parent that match the selector
    const siblings = document.querySelectorAll(groupSelector);

    // Remove selected class from all siblings
    siblings.forEach((sibling) => sibling.classList.remove("selected"));

    // Add selected class to clicked element
    element.classList.add("selected");

    // Map UI values to reason values
    const value = element.querySelector("span").textContent.trim();
    const timeframeSelector = document.getElementById("timeframe-selector");

    if (value === this.getText("sellingSoon")) {
      // Show timeframe selector for selling
      if (timeframeSelector) timeframeSelector.classList.remove("hidden-step");
    } else {
      // Hide timeframe selector for other options
      if (timeframeSelector) timeframeSelector.classList.add("hidden-step");
    }
  }

  /**
   * Show/hide pricing field
   * @param {boolean} show Whether to show the pricing field
   */
  showPricingField(show) {
    const pricingField = document.getElementById("pricing-field");
    if (pricingField) {
      pricingField.classList.toggle("hidden-step", !show);
    }
  }

  /**
   * Initialize map for step 2
   */
  async initializeMap() {
    if (!this.state.selectedProperty) return;

    const mapboxgl = require("mapbox-gl");
    const token = await getMapboxToken();

    // Initialize map with Mapbox GL JS
    mapboxgl.accessToken = token;
    this.map = new mapboxgl.Map({
      container: "map",
      style: "mapbox://styles/mapbox/light-v11",
      center: [
        this.state.selectedProperty.lon,
        this.state.selectedProperty.lat,
      ],
      zoom: 15,
    });

    this.map.on("load", () => {
      this.map.resize();
      // Change color of map elements
      const mapLayers = {
        water: { color: this.config.mainColor, opacity: 0.7 },
        landuse: { color: this.config.mainColor, opacity: 0.2 },
        building: { color: this.config.mainColor, opacity: 0.4 },
      };

      Object.keys(mapLayers).forEach((layer) => {
        this.map.setPaintProperty(layer, "fill-color", mapLayers[layer].color);
        this.map.setPaintProperty(
          layer,
          "fill-opacity",
          mapLayers[layer].opacity
        );
      });
    });

    // Add navigation controls
    this.map.addControl(new mapboxgl.NavigationControl(), "top-right");

    // Add marker for the selected property
    this.marker = new mapboxgl.Marker({ color: this.config.mainColor })
      .setLngLat([
        this.state.selectedProperty.lon,
        this.state.selectedProperty.lat,
      ])
      .addTo(this.map);

    // Add popup to the marker
    new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
    })
      .setLngLat([
        this.state.selectedProperty.lon,
        this.state.selectedProperty.lat,
      ])
      .setHTML(
        `<p style="margin: 0; font-weight: 500;">${this.state.selectedProperty.address}</p>`
      )
      .addTo(this.map);

    // Enable marker dragging
    this.marker.setDraggable(true);

    // Update marker position when dragged
    this.marker.on("dragend", () => {
      const lngLat = this.marker.getLngLat();
      this.state.selectedProperty.lon = lngLat.lng;
      this.state.selectedProperty.lat = lngLat.lat;
    });

    // Add event listener for map click to change marker position
    this.map.on("click", (e) => {
      this.marker.setLngLat(e.lngLat);
      this.state.selectedProperty.lon = e.lngLat.lng;
      this.state.selectedProperty.lat = e.lngLat.lat;
    });
  }

  /**
   * Simulate loading process for step 6
   */
  simulateLoading() {
    const loadingSteps = document.querySelectorAll(".loading-step");
    const progressBar = document.querySelector(".progress-bar-fill");

    // Reset loading steps
    loadingSteps.forEach((step, index) => {
      if (index < 2) {
        step.classList.add("completed");
        step.innerHTML = `<i>✓</i> ${this.getText(
          index === 0 ? "analyzingLocation" : "findingComparables"
        )}`;
      } else {
        step.classList.remove("completed");
        step.innerHTML = `<i>${index === 2 ? "⟳" : "⋯"}</i> ${this.getText(
          index === 2 ? "calculatingTrends" : "finalizingValuation"
        )}`;
      }
    });

    // Simulate step 3 completion after 3 seconds
    setTimeout(() => {
      loadingSteps[2].classList.add("completed");
      loadingSteps[2].innerHTML = `<i>✓</i> ${this.getText(
        "calculatingTrends"
      )}`;
    }, 3000);

    // Simulate step 4 completion after 4.5 seconds
    setTimeout(() => {
      loadingSteps[3].classList.add("completed");
      loadingSteps[3].innerHTML = `<i>✓</i> ${this.getText(
        "finalizingValuation"
      )}`;
    }, 4500);

    // Move to step 7 after 5 seconds
    setTimeout(() => {
      if (progressBar) progressBar.style.width = "100%";
      this.showStep(7);
    }, 5000);
  }

  /**
   * Handle SMS verification process
   */
  handleSmsVerification() {
    // Send SMS code via API
    sendSmsCode(
      this.state.formData.contact.phone,
      this.config.language,
      this.config.token
    )
      .then((result) => {
        this.state.formData.hash = result.hash;
      })
      .catch((error) => {
        console.error("Error sending SMS:", error);
        alert(
          "There was an error sending the verification code. Please try again."
        );
      });

    // Focus on first input field
    setTimeout(() => {
      const firstInput = document.querySelector(".code-input");
      if (firstInput) firstInput.focus();
    }, 300);
  }

  /**
   * Check SMS verification code
   * @param {string} code The code to check
   */
  checkSmsCode(code) {
    // Hide all steps
    document.querySelectorAll(".step-content").forEach((step) => {
      step.classList.add("hidden-step");
    });

    // Show loading step
    document.getElementById("step8b").classList.remove("hidden-step");

    // Adjust progress indicator
    const progressNumber = 7;
    document.querySelectorAll(".step").forEach((indicator, index) => {
      indicator.classList.toggle("active", index < progressNumber);
    });

    // Submit data and update the valuation result
    submitValuation(this.state.formData, this.config)
      .then((result) => {
        this.updateValuationResult(result);
      })
      .catch((error) => {
        console.error("Error submitting valuation:", error);
        alert(
          "There was an error processing your valuation. Please try again."
        );
      });

    // Track the current step
    this.setState({ currentStep: 8 });

    // Send height message to parent
    this.sendHeightToParent();
  }

  /**
   * Update valuation result display
   * @param {Object} result The valuation result
   */
  updateValuationResult(result) {
    const valuationResult = document.querySelector(".valuation-result");
    const confidenceBarFill = document.querySelector(".confidence-bar-fill");
    const confidenceText = document.querySelector(
      ".confidence-level span:last-child"
    );
    const downloadButton = document.querySelector(
      ".result-actions .action-button:first-child"
    );

    if (valuationResult && result) {
      // Format currency based on language
      const formatter = new Intl.NumberFormat(
        this.config.language === "en"
          ? "fr-CH"
          : this.config.language === "fr"
          ? "fr-CH"
          : this.config.language === "de"
          ? "de-CH"
          : this.config.language === "it"
          ? "it-CH"
          : "fr-CH",
        {
          style: "currency",
          currency: "CHF",
          maximumFractionDigits: 0,
        }
      );

      // Update the DOM elements
      valuationResult.textContent = `CHF ${result.price_interval.lower
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, "'")} - ${result.price_interval.upper
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, "'")}`;

      if (confidenceBarFill && result.confidence) {
        // Set confidence level (1-3)
        const confidencePercent = result.confidence * 33; // 1=33%, 2=66%, 3=100%
        confidenceBarFill.style.width = `${confidencePercent}%`;

        // Update confidence text
        if (confidenceText) {
          confidenceText.textContent = this.getText(
            result.confidence === 1
              ? "low"
              : result.confidence === 2
              ? "medium"
              : "high"
          );
        }
      }

      // Add URL to download button
      if (downloadButton && result.url) {
        downloadButton.addEventListener("click", () => {
          window.open(result.url, "_blank");
        });
      }

      // Display valuation
      this.showStep(9);
    }
  }

  /**
   * Prepare and display apartment units for step 3b
   */
  prepareApartmentUnits() {
    const { buildingDetails } = this.state;
    if (
      !buildingDetails ||
      !buildingDetails.ewid ||
      buildingDetails.ewid.length === 0
    ) {
      return;
    }

    // Get the container for apartment floors
    const apartmentFloors = document.querySelector(".apartment-floors");
    if (!apartmentFloors) return;

    // Clear previous content
    apartmentFloors.innerHTML = "";

    // Group apartments by floor
    const apartmentsByFloor = {};

    for (let i = 0; i < buildingDetails.ewid.length; i++) {
      // Skip dwellings that don't exist (wkche equals 0)
      if (buildingDetails.wkche[i] === 0) continue;

      const floorCode = buildingDetails.wstwk[i];
      const floorNumber = this.getFloorNumber(floorCode);

      if (!apartmentsByFloor[floorNumber]) {
        apartmentsByFloor[floorNumber] = [];
      }

      apartmentsByFloor[floorNumber].push({
        id: buildingDetails.ewid[i],
        number: buildingDetails.whgnr[i],
        area: buildingDetails.warea[i],
        rooms: buildingDetails.wazim[i],
        bathrooms: this.estimateBathrooms(buildingDetails.wazim[i]),
        floor: floorNumber,
      });
    }

    // Sort floors in ascending order (ground floor first)
    const sortedFloors = Object.keys(apartmentsByFloor).sort((a, b) => a - b);

    // Add each floor section
    sortedFloors.forEach((floor) => {
      const floorSection = document.createElement("div");
      floorSection.className = "floor-section";

      // Floor title
      const floorTitle = document.createElement("h4");
      floorTitle.textContent = `${this.getText("floor")} ${floor}`;
      floorSection.appendChild(floorTitle);

      // Apartment units container
      const unitsContainer = document.createElement("div");
      unitsContainer.className = "apartment-units";

      // Add each apartment on this floor
      apartmentsByFloor[floor].forEach((apartment) => {
        const apartmentUnit = document.createElement("div");
        apartmentUnit.className = "apartment-unit";
        apartmentUnit.setAttribute("data-id", apartment.id);
        apartmentUnit.setAttribute("data-rooms", apartment.rooms);
        apartmentUnit.setAttribute("data-area", apartment.area);
        apartmentUnit.setAttribute("data-floor", apartment.floor);
        apartmentUnit.onclick = () => this.selectApartmentUnit(apartmentUnit);

        apartmentUnit.innerHTML = `
          <div class="unit-details">
            <span class="unit-name">${
              apartment.number || `Apartment ${apartment.id}`
            }</span>
            <span class="unit-info">${apartment.rooms} ${this.getText(
          "rooms"
        )} · ${apartment.area} m²</span>
          </div>
        `;

        unitsContainer.appendChild(apartmentUnit);
      });

      floorSection.appendChild(unitsContainer);
      apartmentFloors.appendChild(floorSection);
    });

    // Add custom apartment option
    const customSection = document.createElement("div");
    customSection.className = "floor-section";
    customSection.innerHTML = `
      <h4>${this.getText("customApartment")}</h4>
      <div class="apartment-units">
        <div class="apartment-unit custom-apartment">
          <div class="unit-details">
            <span class="unit-name">${this.getText("customApartment")}</span>
            <span class="unit-info">${this.getText("enterCustomDetails")}</span>
          </div>
        </div>
      </div>
    `;

    // Add click handler for custom apartment
    customSection
      .querySelector(".custom-apartment")
      .addEventListener("click", () => {
        this.selectApartmentUnit(null);
      });

    apartmentFloors.appendChild(customSection);
  }

  /**
   * Get floor number from floor code
   * @param {number} floorCode The floor code
   * @returns {number} The floor number
   */
  getFloorNumber(floorCode) {
    // Floor codes: 3101 = 1st floor, 3102 = 2nd floor, etc.
    if (floorCode >= 3101 && floorCode <= 3199) {
      return floorCode - 3100;
    }
    return 0; // Default to ground floor if unknown code
  }

  /**
   * Estimate bathrooms number based on rooms
   * @param {number} rooms Number of rooms
   * @returns {number} Estimated number of bathrooms
   */
  estimateBathrooms(rooms) {
    if (!rooms) return 1;
    if (rooms <= 2) return 1;
    if (rooms <= 4) return 2;
    if (rooms <= 6) return 3;
    return 4;
  }

  /**
   * Select an apartment unit and update form
   * @param {HTMLElement|null} element The selected apartment unit element
   */
  selectApartmentUnit(element) {
    // Clear previous selections
    document.querySelectorAll(".apartment-unit").forEach((unit) => {
      unit.classList.remove("selected");
    });

    if (element === null) {
      // Selected custom apartment
      document.querySelector(".custom-apartment").classList.add("selected");

      // Proceed to step 4 without pre-filling
      setTimeout(() => {
        // Reset form data
        const resetFields = [
          "rooms_nb",
          "living_area",
          "bathrooms_nb",
          "construction_year",
          "land_area",
        ];
        resetFields.forEach(
          (field) => (this.state.formData.property[field] = null)
        );

        // Clear input fields
        [
          "livingAreaInput",
          "landAreaInput",
          "bedroomsInput",
          "bathroomsInput",
          "constructionYearInput",
        ].forEach((id) => {
          const input = document.getElementById(id);
          if (input) input.value = null;
        });

        this.validateStep4Button();
        this.showStep(4);
      }, 300);
      return;
    }

    // Mark selected
    element.classList.add("selected");

    // Get apartment details
    const rooms = element.getAttribute("data-rooms");
    const area = element.getAttribute("data-area");
    const floor = element.getAttribute("data-floor");
    const bathrooms = this.estimateBathrooms(parseFloat(rooms));
    const constructionYear = this.state.buildingDetails.gbauj || null;

    // Store in form data
    this.state.formData.property.rooms_nb = parseFloat(rooms);
    this.state.formData.property.living_area = parseFloat(area);
    this.state.formData.property.bathrooms_nb = bathrooms;
    this.state.formData.property.construction_year = constructionYear;
    this.state.formData.property.floor = floor;

    // Pre-fill step 4 fields
    setTimeout(() => {
      // Update input fields
      const fields = {
        livingAreaInput: area,
        bedroomsInput: rooms,
        bathroomsInput: bathrooms,
        constructionYearInput: constructionYear,
      };

      Object.keys(fields).forEach((id) => {
        const input = document.getElementById(id);
        if (input && fields[id]) input.value = fields[id];
      });

      this.validateStep4Button();
      this.showStep(4);
    }, 300);
  }

  /**
   * Prepare house unit data
   */
  prepareHouseUnit() {
    const { buildingDetails } = this.state;
    if (
      !buildingDetails ||
      !buildingDetails.ewid ||
      buildingDetails.ewid.length === 0
    ) {
      return;
    }

    const area = buildingDetails.warea[0];
    const rooms = buildingDetails.wazim[0];
    const bathrooms = this.estimateBathrooms(buildingDetails.wazim[0]);
    const constructionYear = buildingDetails.gbauj || null;

    // Store in form data
    this.state.formData.property.rooms_nb = parseFloat(rooms);
    this.state.formData.property.living_area = parseFloat(area);
    this.state.formData.property.bathrooms_nb = bathrooms;
    this.state.formData.property.construction_year = constructionYear;

    // Pre-fill step 4 fields
    setTimeout(() => {
      // Update input fields
      const fields = {
        livingAreaInput: area,
        bedroomsInput: rooms,
        bathroomsInput: bathrooms,
        constructionYearInput: constructionYear,
      };

      Object.keys(fields).forEach((id) => {
        const input = document.getElementById(id);
        if (input && fields[id]) input.value = fields[id];
      });

      this.validateStep4Button();
    }, 300);
  }

  /**
   * Override the performReset method for widget-specific reset
   */
  performReset() {
    // Reset to step 1
    this.showStep(1);

    // Clear all inputs
    document.querySelectorAll("input").forEach((input) => {
      if (
        input.type === "text" ||
        input.type === "email" ||
        input.type === "tel" ||
        input.type === "number"
      ) {
        input.value = "";
      } else if (input.type === "checkbox" || input.type === "radio") {
        input.checked = false;
      }
    });

    // Reset select inputs
    document.getElementById("conditionSelect").value = "";
    document.getElementById("propertyTypeSelect").value = "";

    // Clear property form fields
    [
      "bedroomsInput",
      "bathroomsInput",
      "constructionYearInput",
      "livingAreaInput",
      "landAreaInput",
    ].forEach((id) => {
      const element = document.getElementById(id);
      if (element) element.value = null;
    });

    // Clear selections
    document.querySelectorAll(".selected").forEach((element) => {
      element.classList.remove("selected");
    });

    // Reset land area section
    const landAreaSection = document.getElementById("land-area-section");
    if (landAreaSection) landAreaSection.style.display = "none";

    // Reset map if it exists
    if (this.map) {
      this.map.remove();
      this.map = null;
      this.marker = null;
    }

    // Reset state
    this.setState({
      selectedProperty: null,
      buildingDetails: null,
      propertyType: null,
      isApartmentBuilding: false,
      currentStep: 1,
      formData: {
        property: {},
        contact: {},
      },
    });

    // Disable buttons initially
    this.disableButtons();

    // Initialize form data with default values
    this.initializeFormData();
  }
}

// Initialize the widget
const widget = new PropertyValuationWidget();
export default widget;
